import React, { useEffect, useMemo, useState, useRef } from "react";
import api from "../../services/api";
import {
  cancelSubscription,
  undoCancellation,
} from "../../services/paymentService";
import { toast } from "react-toastify";
import { BusinessHoursEditor } from "../../components";
import FaqCampaignEditor from "../../components/FaqCampaignEditor/FaqCampaignEditor";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import styles from "./settings.module.css";
import { useNavigate } from "react-router-dom";
import { Button, ButtonBack, ButtonSave } from "../../components/ui";
import { SchedulingUpsellInline } from "../../components/SchedulingUpsell/SchedulingUpsell";
import {
  isSchedulingIncluded,
  PLAN_UPGRADE_URL,
  resolveBillingMode,
  isSelfServiceBilling,
  resolveBillingModeNotice,
} from "../../utils/planCapabilities";
import { handleFeatureNotIncluded } from "../../utils/notifyPlanUpgrade";

const initialState = {
  name: "",
  segment: "",
  aiPersona: "",
  aiTone: "",
  businessHours: {},
};

const STATUS_LABEL = {
  DRAFT: "Rascunho",
  PENDING: "Aguardando aprovação",
  APPROVED: "Aprovado",
  REJECTED: "Rejeitado",
};

const STATUS_CLASS = {
  DRAFT: "statusDraft",
  PENDING: "statusPending",
  APPROVED: "statusApproved",
  REJECTED: "statusRejected",
};

// Badge humanizado do status da assinatura (NUNCA exibir o valor raw "ACTIVE").
const STATUS_BADGE = {
  ACTIVE: { label: "🟢 Ativo", className: "subActive" },
  TRIAL: { label: "🔵 Período de teste", className: "subTrial" },
  OVERDUE: { label: "🟠 Pagamento em atraso", className: "subOverdue" },
  PENDING_CANCELLATION: {
    label: "🕒 Cancelamento agendado",
    className: "subPendingCancellation",
  },
  CANCELED: { label: "⚫ Cancelado", className: "subCanceled" },
};

// Motivos de cancelamento. O `value` usa as chaves canônicas que o backend
// valida (Zod); o `label` é o texto exibido. O motivo continua opcional.
const CANCEL_REASONS = [
  { value: "preco_alto", label: "O preço está muito alto" },
  { value: "uso_insuficiente", label: "Não estou usando o suficiente" },
  { value: "outra_solucao", label: "Encontrei outra solução" },
  { value: "problemas_tecnicos", label: "Problemas técnicos" },
  { value: "outro", label: "Outro motivo" },
];

function formatDatePtBr(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("pt-BR").format(date);
}

function safeStringify(value) {
  //Para comparar objetos (ex.: businessHours) de forma simples
  return JSON.stringify(value ?? {});
}

// Limites de dias do lembrete de reativação (espelha o Zod do backend: 7..365).
const REVIEW_DAYS_MIN = 7;
const REVIEW_DAYS_MAX = 365;

function clampReviewDays(value) {
  const n = Number.parseInt(value, 10);
  if (Number.isNaN(n)) return REVIEW_DAYS_MIN;
  return Math.min(REVIEW_DAYS_MAX, Math.max(REVIEW_DAYS_MIN, n));
}

// ── Pausa do bot após atendimento humano ────────────────────────────────────
// O valor trafega em MILISSEGUNDOS no campo `humanControlTimeoutMs`. A UI nunca
// expõe "ms": presets são exibidos em min/h e o custom é digitado em MINUTOS.
// Limites espelham o Zod do backend: 5 min (300000 ms) .. 24h (86400000 ms).
const HUMAN_CONTROL_MIN_MS = 300000; // 5 minutos
const HUMAN_CONTROL_MAX_MS = 86400000; // 24 horas
const HUMAN_CONTROL_DEFAULT_MS = 7200000; // 2 horas (default exibido quando null)

// Presets exibidos como botões. `value` em ms; `label` amigável em pt-BR.
const HUMAN_CONTROL_PRESETS = [
  { value: 1800000, label: "30 min" },
  { value: 3600000, label: "1 h" },
  { value: 7200000, label: "2 h" },
  { value: 14400000, label: "4 h" },
];

const MS_PER_MINUTE = 60000;

// Limites do custom em MINUTOS (derivados dos limites em ms acima).
const HUMAN_CONTROL_MIN_MINUTES = HUMAN_CONTROL_MIN_MS / MS_PER_MINUTE; // 5
const HUMAN_CONTROL_MAX_MINUTES = HUMAN_CONTROL_MAX_MS / MS_PER_MINUTE; // 1440

// Texto amigável para um valor em ms (usado nos toasts de sucesso).
function formatHumanControlDuration(ms) {
  const minutes = Math.round(ms / MS_PER_MINUTE);
  if (minutes < 60) return `${minutes} min`;
  if (minutes % 60 === 0) return `${minutes / 60} h`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h${String(m).padStart(2, "0")}`;
}

/**
 * Aplica a máscara de telefone brasileiro `(XX) XXXXX-XXXX` sobre dígitos.
 * Aceita 10 (fixo) ou 11 (celular) dígitos; trunca o excedente.
 * @param {string} digits Apenas dígitos (sem +55).
 * @returns {string} Telefone mascarado parcial conforme o usuário digita.
 */
function maskBrPhone(digits) {
  const d = String(digits ?? "").replace(/\D/g, "").slice(0, 11);
  if (d.length === 0) return "";
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10)
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

export default function SettingsPage() {
  const [status, setStatus] = useState("loading"); //loading | ready | error
  const [saving, setSaving] = useState(false);

  // ── FAQ e campanhas ───────────────────────────────────────────────────────
  // Save DEDICADO: envia apenas { faqItems } (merge parcial seguro no backend).
  const [savingFaq, setSavingFaq] = useState(false);

  const [formData, setFormData] = useState(initialState);
  const initialDataRef = useRef(initialState);

  // 🔴 Esta tela NUNCA é bloqueada: é onde vive o FAQ, o onboarding principal do
  // Plano Atendimento (ADR-0026 §5). O plano só muda a ORDEM e o destaque das
  // seções — e tranca o bloco de Lembrete de Reativação, cuja rota
  // (`PATCH /tenants/reminder-settings`) é gateada por `requireScheduling` no
  // backend.
  //
  // A fonte do plano é o `GET /tenants/me` que esta página JÁ faz (o mesmo
  // `formData.plan` que alimenta o card de uso), e não o AuthContext: o hook
  // `usePlanFeature` exigiria um AuthProvider aqui, e esta tela é montada solta
  // em teste. O predicado é o mesmo dos outros consumidores — invariante 4.
  const schedulingIncluded = isSchedulingIncluded(formData?.plan);
  const faqFirst = !schedulingIncluded;
  const upgradeUrl = PLAN_UPGRADE_URL;

  const [planExpanded, setPlanExpanded] = useState(false);

  // ── Cancelamento de assinatura ────────────────────────────────────────────
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelStep, setCancelStep] = useState(1); // 1 = motivo · 2 = confirmação
  const [cancelReason, setCancelReason] = useState("");
  const [cancelDetail, setCancelDetail] = useState(""); // detalhe livre p/ "outro"
  // Opt-in para desregistrar o número Meta da plataforma ao cancelar. Só faz
  // sentido (e só é exibido) para tenant tradicional — ver `isCoexistence`.
  const [deregisterNumber, setDeregisterNumber] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [undoingCancel, setUndoingCancel] = useState(false);
  const cancelDialogRef = useRef(null);
  const cancelTriggerRef = useRef(null);

  // ── Lembrete de Reativação ────────────────────────────────────────────────
  const [reminderSettings, setReminderSettings] = useState({
    enableReminders: false,
    reviewReminderDays: 90,
  });
  const [reminderSaving, setReminderSaving] = useState(false);
  const [reminderExpanded, setReminderExpanded] = useState(false);

  // ── Pausa do bot após atendimento humano ──────────────────────────────────
  // `selectedMs`: preset selecionado em ms (ou "custom" quando personalizado).
  // `customMinutes`: valor digitado pelo usuário em MINUTOS (unidade amigável).
  const [humanControlExpanded, setHumanControlExpanded] = useState(false);
  const [humanControlSelectedMs, setHumanControlSelectedMs] = useState(
    HUMAN_CONTROL_DEFAULT_MS,
  );
  const [humanControlCustomMinutes, setHumanControlCustomMinutes] = useState(
    String(HUMAN_CONTROL_DEFAULT_MS / MS_PER_MINUTE),
  );
  const [humanControlSaving, setHumanControlSaving] = useState(false);

  // ── Relatório Semanal ─────────────────────────────────────────────────────
  const [reportSettings, setReportSettings] = useState({
    weeklyReportEnabled: true,
    reportPhone: "",
  });
  const [reportSaving, setReportSaving] = useState(false);
  const [reportExpanded, setReportExpanded] = useState(false);

  // ── Desconectar número do Nexus ───────────────────────────────────────────
  // Seção STANDALONE (independente do modal de cancelamento de assinatura).
  // Comportamento condicional:
  //   • Coexistência → instruções no app + botão de re-checar status.
  //   • Tradicional  → botão real (deregister) protegido por ConfirmDialog.
  const [disconnectExpanded, setDisconnectExpanded] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);

  // ── Integração NextFit (apenas segmento "academias") ──────────────────────
  const [nextfitExpanded, setNextfitExpanded] = useState(false);
  const [nextfitApiKey, setNextfitApiKey] = useState("");
  const [hasNextfitKey, setHasNextfitKey] = useState(false);
  const [nextfitSchedulingUrl, setNextfitSchedulingUrl] = useState("");
  const [nextfitSaving, setNextfitSaving] = useState(false);

  // ── Templates de Lembrete ──────────────────────────────────────────────────
  const [templates, setTemplates] = useState([]);
  const [templateExpanded, setTemplateExpanded] = useState(false);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    displayName: "",
    bodyText: "",
  });
  const [savingTemplate, setSavingTemplate] = useState(false);

  const loadTemplates = async () => {
    try {
      const res = await api.get("/templates");
      setTemplates(res.data);
    } catch {
      toast.error("Erro ao carregar templates.");
    }
  };

  useEffect(() => {
    if (templateExpanded && templates.length === 0) loadTemplates();
  }, [templateExpanded]); // eslint-disable-line

  const handleCreateTemplate = async () => {
    if (!templateForm.displayName.trim() || !templateForm.bodyText.trim()) {
      toast.error("Preencha o nome e o corpo do template.");
      return;
    }
    setSavingTemplate(true);
    try {
      const res = await api.post("/templates", templateForm);
      toast.success("Template criado e enviado para aprovação da Meta!");
      if (res.data.warning) toast.warn(res.data.warning, { autoClose: 7000 });
      setTemplates((prev) => [res.data, ...prev]);
      setTemplateForm({ displayName: "", bodyText: "" });
      setShowTemplateForm(false);
    } catch (error) {
      toast.error(error.response?.data?.error || "Erro ao criar template.");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleActivateTemplate = async (id) => {
    try {
      await api.patch(`/templates/${id}/activate`);
      toast.success("Template ativado para lembretes!");
      await loadTemplates();
    } catch (error) {
      toast.error(error.response?.data?.error || "Erro ao ativar template.");
    }
  };

  const handleSyncTemplate = async (id) => {
    try {
      const res = await api.post(`/templates/${id}/sync`);
      toast.success(`Status atualizado: ${STATUS_LABEL[res.data.status]}`);
      setTemplates((prev) => prev.map((t) => (t.id === id ? res.data : t)));
    } catch (error) {
      toast.error(error.response?.data?.error || "Erro ao sincronizar.");
    }
  };

  const handleSubmitTemplate = async (id) => {
    try {
      const res = await api.post(`/templates/${id}/submit`);
      toast.success("Template enviado para aprovação!");
      setTemplates((prev) => prev.map((t) => (t.id === id ? res.data : t)));
    } catch (error) {
      toast.error(error.response?.data?.error || "Erro ao submeter template.");
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm("Remover este template?")) return;
    try {
      await api.delete(`/templates/${id}`);
      toast.success("Template removido.");
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch (error) {
      toast.error(error.response?.data?.error || "Erro ao remover.");
    }
  };

  const navigate = useNavigate();

  async function loadData() {
    setStatus("loading");
    try {
      const res = await api.get("/tenants/me");
      const data = { ...res.data };
      setFormData(data);
      initialDataRef.current = data;

      setReminderSettings({
        enableReminders: data?.tenant?.enableReminders ?? false,
        // Sem hardcode: usa o valor do backend; 90 só como fallback visual se null.
        reviewReminderDays: data?.tenant?.reviewReminderDays ?? 90,
      });

      setReportSettings({
        weeklyReportEnabled: data?.tenant?.weeklyReportEnabled ?? true,
        // Armazenamos apenas dígitos; a UI exibe com máscara (XX) XXXXX-XXXX.
        reportPhone: (data?.tenant?.reportPhone ?? "").replace(/\D/g, ""),
      });

      // Pausa do bot após atendimento humano. null → usa o default de 2h só
      // para exibição (sem forçar gravação). Se o valor não bater com nenhum
      // preset, cai em "Personalizado" e preenche o campo em minutos.
      const rawTimeout = data?.tenant?.humanControlTimeoutMs;
      const effectiveTimeoutMs =
        rawTimeout == null ? HUMAN_CONTROL_DEFAULT_MS : Number(rawTimeout);
      const matchesPreset = HUMAN_CONTROL_PRESETS.some(
        (p) => p.value === effectiveTimeoutMs,
      );
      setHumanControlSelectedMs(matchesPreset ? effectiveTimeoutMs : "custom");
      setHumanControlCustomMinutes(
        String(Math.round(effectiveTimeoutMs / MS_PER_MINUTE)),
      );

      // Estado "conectado" baseia-se apenas no booleano; a key nunca trafega.
      setHasNextfitKey(Boolean(data?.tenant?.hasNextfitKey));
      // O link de agendamento é público — pode ser carregado e exibido.
      setNextfitSchedulingUrl(data?.tenant?.nextfitSchedulingUrl ?? "");

      setStatus("ready");
    } catch (e) {
      setStatus("error");
      toast.error("Erro ao carregar configurações.");
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  const handleChangeSubscription = () => {
    navigate("/subscription/billing");
  };

  const openCancelModal = () => {
    setCancelReason("");
    setCancelDetail("");
    setDeregisterNumber(false);
    setCancelStep(1);
    setCancelModalOpen(true);
  };

  const closeCancelModal = () => {
    if (canceling) return; // não fechar durante a request em andamento
    setCancelModalOpen(false);
    setCancelStep(1);
    setCancelReason("");
    setCancelDetail("");
    setDeregisterNumber(false);
  };

  // Foco no modal ao abrir + fechar por Esc (acessibilidade).
  useEffect(() => {
    if (!cancelModalOpen) return;
    cancelTriggerRef.current = document.activeElement;
    cancelDialogRef.current?.focus();

    function onKeyDown(e) {
      if (e.key === "Escape") closeCancelModal();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      // Devolve o foco ao gatilho ao fechar.
      if (cancelTriggerRef.current?.focus) cancelTriggerRef.current.focus();
    };
  }, [cancelModalOpen]); // eslint-disable-line

  const handleConfirmCancel = async () => {
    setCanceling(true);
    try {
      // Monta o payload com o motivo (UX opcional). `detail` só faz sentido
      // quando o motivo é "outro".
      const payload = {};
      if (cancelReason) payload.reason = cancelReason;
      if (cancelReason === "outro" && cancelDetail?.trim())
        payload.detail = cancelDetail;

      // Espelha a validação do backend: o deregister só vale para tenant
      // tradicional. Mesmo que o estado escape, nunca enviamos `true` para
      // coexistência (o checkbox nem é exibido nesse caso).
      if (deregisterNumber && !isCoexistence) {
        payload.deregisterNumber = true;
      }

      const res = await cancelSubscription(
        Object.keys(payload).length ? payload : undefined,
      );
      // O backend distingue os dois fluxos (ADR-0019):
      // - ACTIVE → AGENDA: assinatura vira PENDING_CANCELLATION, acesso segue até
      //   o fim do período pago (`cancelAt`).
      // - OVERDUE → encerra IMEDIATAMENTE: assinatura vira CANCELED (immediate),
      //   sem período pago a manter e sem "Desfazer".
      const immediate =
        res?.data?.immediate === true || res?.data?.status === "CANCELED";
      setFormData((prev) => ({
        ...prev,
        tenant: {
          ...prev.tenant,
          subscriptionStatus: immediate
            ? "CANCELED"
            : "PENDING_CANCELLATION",
          cancelAt: immediate
            ? null
            : (res?.data?.cancelAt ?? prev.tenant?.cancelAt),
        },
      }));
      if (immediate) {
        toast.success(
          "Assinatura encerrada. Como o pagamento estava pendente, não havia período pago a manter.",
        );
      } else {
        const dateStr =
          formatDatePtBr(res?.data?.cancelAt) ||
          formatDatePtBr(formData?.tenant?.nextBillingDate);
        toast.success(
          dateStr
            ? `Cancelamento agendado. Seu acesso continua até ${dateStr}.`
            : "Cancelamento agendado. Seu acesso continua até o fim do período pago.",
        );
      }
      // O backend pode devolver um aviso (ex.: desconexão de coexistência é
      // feita pelo próprio app WhatsApp Business). Exibimos quando presente.
      if (res?.data?.warning) {
        toast.warn(res.data.warning, { autoClose: 8000 });
      }
      setCancelModalOpen(false);
      setCancelStep(1);
      setCancelReason("");
      setCancelDetail("");
      setDeregisterNumber(false);
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Erro ao cancelar a assinatura. Tente novamente.",
      );
    } finally {
      setCanceling(false);
    }
  };

  // Desfaz um cancelamento agendado (ADR-0019): volta a assinatura para ACTIVE.
  const handleUndoCancellation = async () => {
    setUndoingCancel(true);
    try {
      await undoCancellation();
      setFormData((prev) => ({
        ...prev,
        tenant: {
          ...prev.tenant,
          subscriptionStatus: "ACTIVE",
          cancelAt: null,
        },
      }));
      toast.success("Cancelamento desfeito. Sua assinatura segue ativa.");
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Não foi possível desfazer o cancelamento. Tente novamente.",
      );
    } finally {
      setUndoingCancel(false);
    }
  };

  const isDirty = useMemo(() => {
    //Comparação simples
    return safeStringify(formData) !== safeStringify(initialDataRef.current);
  }, [formData]);

  const canSave = status === "ready" && isDirty && !saving;

  useEffect(() => {
    function onBeforeUnload(e) {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!canSave) return;

    setSaving(true);
    try {
      // 1. Enviamos apenas os dados do tenant
      const res = await api.patch("/tenants/update", formData.tenant);

      // 2. IMPORTANTE: o backend pode retornar o objeto atualizado
      // Vamos atualizar o formData com o que veio do banco para garantir sincronia
      const updatedData = {
        ...formData,
        tenant: {
          ...formData.tenant,
          ...res.data,
        },
      };

      setFormData(updatedData);
      initialDataRef.current = JSON.parse(JSON.stringify(updatedData));

      toast.success("Configurações salvas!");
    } catch (error) {
      toast.error("Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  // Save DEDICADO de FAQ/campanhas. Envia SÓ { faqItems } — o backend preserva
  // os demais campos (Prisma ignora `undefined`). Atualiza otimista e reconcilia
  // em background com o que o sanitizer do backend normalizou.
  const handleSaveFaq = async (faqItems) => {
    setSavingFaq(true);
    try {
      await api.patch("/tenants/update", { faqItems });
      setFormData((prev) => ({
        ...prev,
        tenant: { ...prev.tenant, faqItems },
      }));
      toast.success("FAQ e campanhas salvos!");
      // Reconcilia em background (não bloqueia a UI).
      try {
        const res = await api.get("/tenants/me");
        setFormData((prev) => ({
          ...prev,
          tenant: {
            ...prev.tenant,
            faqItems: res.data?.tenant?.faqItems ?? faqItems,
          },
        }));
      } catch (_) {
        // Reconciliação é best-effort; o save já foi confirmado ao usuário.
      }
    } catch (err) {
      const msg =
        err?.response?.status === 400
          ? (err.response.data?.error || "Dados inválidos.") +
            (err.response.data?.details
              ? ` (${JSON.stringify(err.response.data.details)})`
              : "")
          : "Erro ao salvar FAQ.";
      toast.error(msg);
    } finally {
      setSavingFaq(false);
    }
  };

  async function handleSaveReminderSettings() {
    setReminderSaving(true);
    try {
      await api.patch("/tenants/reminder-settings", {
        enableReminders: reminderSettings.enableReminders,
        reviewReminderDays: clampReviewDays(reminderSettings.reviewReminderDays),
      });
      toast.success(
        "Configurações salvas! Os lembretes serão enviados todo dia às 10h.",
      );
    } catch (error) {
      // Defesa em profundidade (ADR-0026): o bloco já aparece trancado quando o
      // plano não inclui agendamento, mas se o plano ainda não hidratou o
      // usuário pode chegar aqui. Mensagem humana + CTA, nunca o erro cru.
      if (!handleFeatureNotIncluded(error)) {
        toast.error(
          error.response?.data?.error ||
            "Erro ao salvar configurações de lembrete.",
        );
      }
    } finally {
      setReminderSaving(false);
    }
  }

  async function handleSaveHumanControlSettings() {
    // Resolve o valor em ms a partir do preset OU do custom (digitado em min).
    let timeoutMs;
    if (humanControlSelectedMs === "custom") {
      const minutes = Number(humanControlCustomMinutes);
      // Validação defensiva (espelha o backend): número finito e positivo.
      if (!Number.isFinite(minutes) || minutes <= 0) {
        toast.error("Informe um tempo válido (em minutos).");
        return;
      }
      timeoutMs = Math.round(minutes * MS_PER_MINUTE);
    } else {
      timeoutMs = Number(humanControlSelectedMs);
    }

    // Rejeita fora dos limites (5 min .. 24h) antes de enviar — evita 400.
    if (timeoutMs < HUMAN_CONTROL_MIN_MS || timeoutMs > HUMAN_CONTROL_MAX_MS) {
      toast.error(
        `O tempo deve ficar entre ${HUMAN_CONTROL_MIN_MINUTES} minutos e 24 horas.`,
      );
      return;
    }

    setHumanControlSaving(true);
    try {
      await api.patch("/tenants/human-control-settings", {
        humanControlTimeoutMs: timeoutMs,
      });
      toast.success(
        `Configuração salva! O bot ficará em silêncio por ${formatHumanControlDuration(
          timeoutMs,
        )} após um atendimento humano.`,
      );
    } catch (error) {
      toast.error(
        error.response?.data?.error ||
          "Erro ao salvar a pausa do bot após atendimento humano.",
      );
    } finally {
      setHumanControlSaving(false);
    }
  }

  async function handleSaveReportSettings() {
    setReportSaving(true);
    try {
      await api.patch("/tenants/report-settings", {
        weeklyReportEnabled: reportSettings.weeklyReportEnabled,
        // Vazio → null (remove o número; o job não enviará).
        reportPhone: reportSettings.reportPhone || null,
      });
      toast.success(
        "Configurações salvas! O próximo relatório será enviado na segunda-feira às 9h.",
      );
    } catch (error) {
      toast.error(
        error.response?.data?.error ||
          "Erro ao salvar configurações de relatório.",
      );
    } finally {
      setReportSaving(false);
    }
  }

  // ── Desconectar número ─────────────────────────────────────────────────────
  // Mescla o tenant retornado pela API no formData (preservando o restante).
  function mergeTenant(tenant) {
    if (!tenant) return;
    setFormData((prev) => {
      const next = { ...prev, tenant: { ...prev.tenant, ...tenant } };
      // Mantém o snapshot inicial alinhado para o cálculo de "isDirty".
      initialDataRef.current = {
        ...initialDataRef.current,
        tenant: { ...initialDataRef.current.tenant, ...tenant },
      };
      return next;
    });
  }

  // Caso TRADICIONAL: desregistra o número da Cloud API da Meta (deregister).
  // ⚠️ Shape REAL do backend: { success, message } — NÃO retorna `tenant`.
  // Erros vêm em `error.response.data.message`. Em sucesso, re-buscamos /me
  // para refletir o status real (connected:false) no estado.
  async function handleDisconnectNumber() {
    setDisconnecting(true);
    try {
      await api.post("/meta/deregister");
      // Re-fetch da fonte da verdade (mesmo padrão do loadData) para refletir
      // o novo status. loadData também reseta initialDataRef → isDirty correto.
      await loadData();
      toast.success(
        "Número desconectado. O bot não responderá mais por este número.",
      );
      setShowDisconnectConfirm(false);
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Não foi possível desconectar o número agora. Tente novamente.",
      );
    } finally {
      setDisconnecting(false);
    }
  }

  // Caso COEXISTÊNCIA: o usuário desconecta pelo app; aqui só re-checamos o
  // status junto à Meta (POST /tenants/whatsapp/refresh-status).
  async function handleRefreshWhatsappStatus() {
    setCheckingStatus(true);
    try {
      const res = await api.post("/tenants/whatsapp/refresh-status");
      const tenant = res.data?.tenant;
      mergeTenant(tenant);
      if (tenant?.connected === false) {
        toast.success("Número desconectado com sucesso.");
      } else {
        toast.success("Status atualizado.");
      }
    } catch (error) {
      const code = error.response?.status;
      if (code === 422) {
        toast.error("Este número não está conectado à plataforma.");
      } else if (code === 502) {
        toast.error(
          "Não foi possível verificar o status agora. Tente novamente em instantes.",
        );
      } else {
        toast.error(
          error.response?.data?.error ||
            "Não foi possível verificar o status agora.",
        );
      }
    } finally {
      setCheckingStatus(false);
    }
  }

  async function handleSaveNextfitSettings() {
    const trimmedKey = nextfitApiKey.trim();
    const trimmedUrl = nextfitSchedulingUrl.trim();

    // Nada a salvar se não há key digitada e o link continua vazio.
    if (!trimmedKey && !trimmedUrl) {
      toast.warning("Nada para salvar.");
      return;
    }

    // Validação leve de URL no cliente (UX); a fonte da verdade é o backend.
    if (trimmedUrl && !/^https?:\/\//i.test(trimmedUrl)) {
      toast.warning("O link deve começar com http:// ou https://");
      return;
    }

    // Update parcial: só envia a key se digitada; o link sempre que tocado
    // (string trimada, ou null para remover).
    const payload = {};
    if (trimmedKey) {
      payload.nextfitApiKey = trimmedKey;
    }
    payload.nextfitSchedulingUrl = trimmedUrl ? trimmedUrl : null;

    setNextfitSaving(true);
    try {
      const res = await api.patch("/tenants/nextfit-settings", payload);
      setHasNextfitKey(Boolean(res.data?.hasNextfitKey));
      // Reflete o link salvo (não limpamos — diferente da key).
      setNextfitSchedulingUrl(res.data?.nextfitSchedulingUrl ?? "");
      // Nunca mantemos a key em estado após o envio — limpamos o input.
      setNextfitApiKey("");
      toast.success("Integração NextFit salva com sucesso!");
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(
          "Integração NextFit disponível apenas para o segmento Academia.",
        );
      } else {
        toast.error(
          error.response?.data?.message ||
            "Erro ao salvar a integração NextFit.",
        );
      }
    } finally {
      setNextfitSaving(false);
    }
  }

  /* ── Perguntas frequentes e campanhas ─────────────────────────────────────
     Editor dedicado de `faqItems`. Save independente do form (envia SÓ
     { faqItems } → merge parcial seguro). O `key` força o remount após salvar
     para que o dirty-check interno do editor re-sincronize com o array
     normalizado pelo backend.

     PROEMINÊNCIA (ADR-0026 §5): para quem não agenda, o FAQ é o onboarding
     principal — não um detalhe no meio da página. Por isso a MESMA seção é
     renderizada como PRIMEIRA do formulário quando `faqFirst`, com "Comece por
     aqui" e copy própria. Uma variável só (não dois blocos duplicados) para não
     haver duas versões do editor divergindo com o tempo. */
  const faqSection = (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>
        {faqFirst && "⭐ "}Perguntas frequentes e campanhas
      </h2>
      <p className={styles.helpText} style={{ marginBottom: "12px" }}>
        {faqFirst
          ? "Comece por aqui: é daqui que o assistente responde seus clientes no WhatsApp. Cadastre horário, endereço, formas de pagamento e o que mais te perguntam. FAQs ficam sempre ativas; campanhas têm data de validade e deixam de ser usadas após expirarem."
          : "Cadastre respostas prontas para o assistente. FAQs ficam sempre ativas; campanhas têm data de validade (ex.: colônia de férias) e deixam de ser usadas após expirarem."}
      </p>
      <FaqCampaignEditor
        key={JSON.stringify(formData?.tenant?.faqItems ?? [])}
        initialItems={formData?.tenant?.faqItems ?? []}
        onSave={handleSaveFaq}
        saving={savingFaq}
      />
    </section>
  );

  const usageBar = (used, total, label) => {
    const usedNum = Number(used ?? 0);
    const totalNum = Number(total ?? 0);
    const percentage =
      totalNum > 0 ? Math.min((usedNum / totalNum) * 100, 100) : 0;
    const color = percentage > 85 ? "#ff4d4f" : "#25d366";

    return (
      <div style={{ marginBottom: "12px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "0.85rem",
            fontWeight: "600",
            color: "var(--text-secondary)",
            marginBottom: "6px",
          }}
        >
          <span>{label}</span>
          <span>
            {usedNum} / {totalNum}
          </span>
        </div>
        <div
          role="progressbar"
          aria-valuenow={usedNum}
          aria-valuemin="0"
          aria-valuemax={totalNum}
          aria-label={label}
          style={{
            width: "100%",
            backgroundColor: "#eaecf0",
            height: "8px",
            borderRadius: "4px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${percentage}%`,
              backgroundColor: color,
              height: "100%",
              transition: "width 0.4s ease-out",
            }}
          />
        </div>
      </div>
    );
  };

  if (status === "loading") {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.skeletonTitle} />
          <div className={styles.skeletonTextarea} />
          <div className={styles.skeletonTextarea} />
          <div className={styles.skeletonButton} />
          <p className={styles.loadingText}>Carregando configurações...</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h1 className={styles.title}>Configurações do Negócio</h1>
          <p className={styles.errorText}>
            Não foi possível carregar suas configurações
          </p>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={loadData}
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  const personaLength = formData.aiPersona?.length ?? 0;
  const personaMax = 800;
  const isAcademia = formData?.tenant?.segment === "academias";
  // Tradicional = coexistence === false. O campo vem de GET /tenants/me.
  // `=== true` evita tratar undefined (estado carregando) como coexistência.
  const isCoexistence = formData?.tenant?.coexistence === true;
  // Há número Meta vinculado? Sem ele, desregistrar não faz sentido.
  const hasMetaNumber = Boolean(formData?.tenant?.metaPhoneNumberId);

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          {/* Sem agendamento, /agenda cai na tela de upsell — voltar para lá
              seria um beco. Esta tela é a casa do tier; o "voltar" vai para a
              visão geral. */}
          <ButtonBack to={schedulingIncluded ? "/agenda" : "/visao-geral"} />
          <h1 className={styles.title}>Configurações do Negócio</h1>

          {isDirty ? (
            <span
              className={styles.dirtyBadge}
              role="status"
              aria-live="polite"
            >
              Alterações não salvas
            </span>
          ) : (
            <span
              className={styles.savedBadge}
              role="status"
              aria-live="polite"
            >
              Tudo salvo
            </span>
          )}
        </div>

        {/* Criar <div> para gerenciamento do plano */}
        <div className={styles.plan} aria-label="tenant-plan">
          <button
            type="button"
            className={styles.planToggle}
            aria-expanded={planExpanded}
            onClick={() => setPlanExpanded((v) => !v)}
          >
            <span className={styles.label}>Plano Atual </span>
            <span className={styles.toggleIcon}>
              {planExpanded ? "▾" : "▸"}
            </span>
          </button>

          {planExpanded && (
            <div className={styles.planContent}>
              <div className={styles.planData}>
                <div className={styles.planDataDetails}>
                  <p className={styles.text}>Plano Atual </p>
                  <span className={styles.planText}>
                    {formData?.plan?.name || ""}
                  </span>
                </div>
                {/* "Próxima Renovação" só existe onde existe cobrança: em
                    MANUAL/INTERNAL o `nextBillingDate` é NULL por DECISÃO
                    (ADR-0034 §6 — é data financeira, não data de concessão de
                    acesso), e o "Data não disponível" que caía aqui acusava
                    falha de carregamento onde não há o que carregar. A linha
                    some; quem explica é o aviso de modo, logo abaixo, cuja copy
                    é a canônica do `resolveBillingModeNotice` (invariante 4).
                    Mesmo predicado — e mesmo fail-safe `ASAAS` — do CTA de
                    troca abaixo: "não sei" mantém a linha visível. */}
                {isSelfServiceBilling(resolveBillingMode(formData?.tenant)) && (
                  <div className={styles.planDataDetails}>
                    <p className={styles.text}>Próxima Renovação</p>
                    <span className={styles.planText}>
                      {formData?.tenant?.nextBillingDate
                        ? new Intl.DateTimeFormat("pt-BR").format(
                            new Date(formData.tenant.nextBillingDate),
                          )
                        : "Data não disponível"}
                    </span>
                  </div>
                )}
              </div>
              <div className={styles.planUsage}>
                <p className={styles.text}>Uso Atual</p>
                <div className={styles.planUsageData}>
                  <div className={styles.planUsageDetails}>
                    {usageBar(
                      formData?.usage?.messagesSent,
                      formData?.plan?.messagesLimit,
                      "Mensagens Enviadas",
                    )}
                    {usageBar(
                      formData?.usage?.appointmentsCount,
                      formData?.plan?.appointmentsCount || 1000,
                      "Agendamentos",
                    )}
                  </div>
                </div>
              </div>

              {/* Troca de plano é self-service financeiro (ADR-0034 §7): em
                  MANUAL/INTERNAL ela é operação administrativa e não existe
                  aqui. O aviso do modo, logo abaixo, diz por quê. */}
              {isSelfServiceBilling(resolveBillingMode(formData?.tenant)) && (
                <div className={styles.buttonContainer}>
                  <Button variant="primary" onClick={handleChangeSubscription}>
                    Mudar Plano
                  </Button>
                </div>
              )}

              {/* ── Minha Assinatura ──────────────────────────────────────── */}
              {(() => {
                const subStatus = formData?.tenant?.subscriptionStatus;
                const badge = STATUS_BADGE[subStatus];
                const nextBilling = formatDatePtBr(
                  formData?.tenant?.nextBillingDate,
                );
                const trialEnd = formatDatePtBr(
                  formData?.tenant?.trialExpiration,
                );
                const cancelAtDate =
                  formatDatePtBr(formData?.tenant?.cancelAt) || nextBilling;
                // Autoridade financeira (ADR-0034). Fail-safe `ASAAS` vive no
                // `resolveBillingMode` — aqui ninguém compara a string solta.
                const billingMode = resolveBillingMode(formData?.tenant);
                const selfServiceBilling = isSelfServiceBilling(billingMode);
                const modeNotice = resolveBillingModeNotice(billingMode);

                return (
                  <div className={styles.subscription} aria-label="minha-assinatura">
                    <div className={styles.subscriptionHeader}>
                      <p className={styles.text}>Minha Assinatura</p>
                      {badge ? (
                        <span
                          className={`${styles.statusBadge} ${styles[badge.className]}`}
                          role="status"
                          aria-live="polite"
                        >
                          {badge.label}
                        </span>
                      ) : null}
                    </div>

                    {/* Aviso de modo de cobrança (ADR-0034). Informativo, não
                        bloqueio: reusa `.subscriptionInfo`, o estilo neutro que
                        esta tela já tem — nada de warning/danger. */}
                    {modeNotice && (
                      <p className={styles.subscriptionInfo} role="status">
                        <strong>{modeNotice.title}.</strong> {modeNotice.text}
                      </p>
                    )}

                    {subStatus === "TRIAL" && (
                      <p className={styles.subscriptionInfo}>
                        {trialEnd
                          ? `Seu período de teste encerra em ${trialEnd}.`
                          : "Você está no período de teste."}
                      </p>
                    )}

                    {(subStatus === "ACTIVE" || subStatus === "OVERDUE") && (
                      <>
                        {subStatus === "OVERDUE" && (
                          <p className={styles.subscriptionWarning} role="alert">
                            Seu pagamento está em atraso. Regularize para manter
                            o acesso.
                          </p>
                        )}
                        {/* Próxima cobrança e cancelamento só existem quando
                            há cobrança: em MANUAL/INTERNAL o `nextBillingDate`
                            é NULL por decisão (ADR-0034 §6) e não há assinatura
                            no Asaas para cancelar (§4). */}
                        {selfServiceBilling && (
                          <>
                            <p className={styles.subscriptionInfo}>
                              {nextBilling
                                ? `Próxima cobrança: ${nextBilling}`
                                : "Próxima cobrança: data não disponível"}
                            </p>
                            <button
                              type="button"
                              className={styles.cancelLink}
                              onClick={openCancelModal}
                            >
                              Cancelar assinatura
                            </button>
                          </>
                        )}
                      </>
                    )}

                    {subStatus === "PENDING_CANCELLATION" && (
                      <>
                        <p className={styles.subscriptionInfo}>
                          {cancelAtDate
                            ? `Cancelamento agendado para ${cancelAtDate}. Seu acesso continua até lá.`
                            : "Cancelamento agendado. Seu acesso continua até o fim do período pago."}
                        </p>
                        {selfServiceBilling && (
                          <div className={styles.buttonContainer}>
                            <Button
                              variant="secondary"
                              onClick={handleUndoCancellation}
                              isLoading={undoingCancel}
                            >
                              Desfazer cancelamento
                            </Button>
                          </div>
                        )}
                      </>
                    )}

                    {/* Reativação é compra: não se oferece a quem não é cobrado
                        por aqui — para esses, o aviso do modo acima já diz que
                        a mudança passa pelo suporte. */}
                    {subStatus === "CANCELED" && selfServiceBilling && (
                      <>
                        <p className={styles.subscriptionInfo}>
                          Sua assinatura foi cancelada. Você pode reativar a
                          qualquer momento escolhendo um plano abaixo.
                        </p>
                        <div className={styles.buttonContainer}>
                          <Button
                            variant="primary"
                            onClick={handleChangeSubscription}
                          >
                            Ver planos disponíveis
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* ── Modal de cancelamento (2 passos) ───────────────────────────── */}
        {cancelModalOpen && (
          <div
            className={styles.modalOverlay}
            onClick={closeCancelModal}
            role="presentation"
          >
            <div
              className={styles.modal}
              role="dialog"
              aria-modal="true"
              aria-labelledby="cancelModalTitle"
              tabIndex={-1}
              ref={cancelDialogRef}
              onClick={(e) => e.stopPropagation()}
            >
              {cancelStep === 1 ? (
                <>
                  <h2 id="cancelModalTitle" className={styles.modalTitle}>
                    Por que você quer cancelar?
                  </h2>
                  <p className={styles.helpText}>
                    Conte para nós o motivo (opcional). Isso nos ajuda a
                    melhorar.
                  </p>

                  <fieldset className={styles.reasonFieldset}>
                    <legend className={styles.srOnly}>
                      Motivo do cancelamento
                    </legend>
                    {CANCEL_REASONS.map(({ value, label }) => (
                      <label key={value} className={styles.reasonOption}>
                        <input
                          type="radio"
                          name="cancelReason"
                          value={value}
                          checked={cancelReason === value}
                          onChange={(e) => setCancelReason(e.target.value)}
                        />
                        {label}
                      </label>
                    ))}
                  </fieldset>

                  {cancelReason === "outro" && (
                    <div
                      className={styles.field}
                      style={{ marginTop: "0.875rem" }}
                    >
                      <label className={styles.label} htmlFor="cancelDetail">
                        Conte mais (opcional)
                      </label>
                      <textarea
                        id="cancelDetail"
                        className={styles.textarea}
                        rows={3}
                        maxLength={500}
                        placeholder="O que motivou seu cancelamento?"
                        value={cancelDetail}
                        onChange={(e) => setCancelDetail(e.target.value)}
                        aria-describedby="cancelDetail-counter"
                      />
                      <span
                        id="cancelDetail-counter"
                        className={styles.counter}
                      >
                        {cancelDetail.length}/500
                      </span>
                    </div>
                  )}

                  <div className={styles.modalActions}>
                    <Button
                      variant="secondary"
                      onClick={closeCancelModal}
                    >
                      Manter minha assinatura
                    </Button>
                    <Button variant="ghost" onClick={() => setCancelStep(2)}>
                      Continuar
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <h2 id="cancelModalTitle" className={styles.modalTitle}>
                    Tem certeza que deseja cancelar?
                  </h2>
                  {formData?.tenant?.subscriptionStatus === "OVERDUE" ? (
                    // Inadimplente: NÃO há período pago a manter. Nunca prometer
                    // "acesso continua até" — o cancelamento encerra na hora.
                    <ul className={styles.consequencesList}>
                      <li>Sua assinatura está com pagamento pendente</li>
                      <li>Ao cancelar, ela é encerrada imediatamente</li>
                      <li>Sua conta será desativada agora</li>
                      <li>Seus dados ficam salvos por 30 dias</li>
                    </ul>
                  ) : (
                    <ul className={styles.consequencesList}>
                      <li>Sua assinatura não será renovada</li>
                      {formatDatePtBr(formData?.tenant?.nextBillingDate) && (
                        <li>
                          Você continua com acesso até{" "}
                          {formatDatePtBr(formData?.tenant?.nextBillingDate)}
                        </li>
                      )}
                      <li>Após essa data, sua conta será desativada</li>
                      <li>Seus dados ficam salvos por 30 dias</li>
                    </ul>
                  )}

                  {/* Offboarding do número WhatsApp.
                      • Tradicional (coexistence === false) com número Meta:
                        opt-in para desregistrar o número da plataforma.
                      • Coexistência: NÃO oferecemos deregister — a desconexão
                        é feita pelo próprio app WhatsApp Business. */}
                  {isCoexistence ? (
                    <p
                      className={styles.helpText}
                      style={{ marginTop: "0.25rem" }}
                    >
                      ℹ️ Sua conta está em modo coexistência. Para desconectar o
                      número, faça isso pelo próprio app do WhatsApp Business — a
                      plataforma não desregistra números em coexistência.
                    </p>
                  ) : (
                    hasMetaNumber && (
                      <div
                        className={styles.reportToggleRow}
                        style={{ marginTop: "0.25rem" }}
                      >
                        <label
                          htmlFor="deregisterNumber"
                          className={styles.reportToggleLabel}
                        >
                          <input
                            id="deregisterNumber"
                            type="checkbox"
                            className={styles.reportCheckbox}
                            checked={deregisterNumber}
                            disabled={canceling}
                            onChange={(e) =>
                              setDeregisterNumber(e.target.checked)
                            }
                            aria-describedby="deregisterNumber-help"
                          />
                          Também desregistrar meu número do WhatsApp da
                          plataforma
                        </label>
                        <p
                          id="deregisterNumber-help"
                          className={styles.helpText}
                          style={{ marginTop: "0.375rem" }}
                        >
                          Isso encerra a integração do seu número com a Nexus na
                          Cloud API da Meta. Se ficar desmarcado, o número
                          permanece registrado.
                        </p>
                      </div>
                    )
                  )}

                  <div className={styles.modalActions}>
                    <Button
                      variant="secondary"
                      onClick={closeCancelModal}
                      disabled={canceling}
                    >
                      Manter minha assinatura
                    </Button>
                    <Button
                      variant="danger"
                      onClick={handleConfirmCancel}
                      isLoading={canceling}
                    >
                      {canceling ? "Cancelando..." : "Sim, cancelar assinatura"}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleSave} className={styles.form}>
          {faqFirst && faqSection}

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Informações Básicas</h2>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="name">
                Nome da Empresa <span className={styles.required}>*</span>
              </label>

              <input
                id="name"
                type="text"
                className={styles.input}
                value={formData?.tenant?.name || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tenant: {
                      ...formData.tenant,
                      name: e.target.value,
                    },
                  })
                }
                placeholder="Ex.: Barbearia do João"
                autoComplete="organization"
                aria-describedby="name-help"
              />

              <p id="name-help" className={styles.helpText}>
                Este nome pode aparecer para sua equipe e em telas internas
              </p>
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="segment">
                Segmento
              </label>

              <select
                id="segment"
                name="segment"
                value={formData?.tenant?.segment || ""}
                className={styles.select}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tenant: {
                      ...formData.tenant,
                      segment: e.target.value,
                    },
                  })
                }
              >
                <option value="">Selecione o tipo de negócio...</option>
                <option value="barbearias">✂️ Barbearia / Salão</option>
                <option value="consultorios">🏥 Consultório / Clínica</option>
                <option value="mecanicas">🔧 Mecânica / Auto Center</option>
                <option value="lava_jatos">🚗 Lava-Jato / Estética Automotiva</option>
                <option value="academias">🏋️ Academia / Escola Esportiva</option>
              </select>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Inteligência Artificial</h2>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="aiTone">
                Tom da IA
              </label>

              <select
                id="aiTone"
                name="aiTone"
                value={formData?.tenant?.aiTone || "Objetiva"}
                className={styles.select}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tenant: {
                      ...formData.tenant,
                      aiTone: e.target.value,
                    },
                  })
                }
              >
                <option value="Objetiva">Objetiva (Direto ao ponto)</option>
                <option value="Educada">Educada (Cordial e formal)</option>
                <option value="Descontraída">
                  Descontraída (Usa emojis e gírias)
                </option>
              </select>
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="aiPersona">
                Personalidade da IA (Persona)
              </label>

              <textarea
                id="aiPersona"
                className={styles.textarea}
                rows={4}
                placeholder="Ex.: Você é um assistente simpático de uma barbearia..."
                value={formData?.tenant?.aiPersona || ""}
                maxLength={personaMax}
                aria-describedby="aiPersona-help"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tenant: {
                      ...formData.tenant,
                      aiPersona: e.target.value,
                    },
                  })
                }
              />

              <div className={styles.metaRow}>
                <p id="aiPersona-help" className={styles.helpText}>
                  Dica: descreva tom, regras e exemplos de resposta.
                </p>
                <span className={styles.counter}>
                  {personaLength}/{personaMax}
                </span>
              </div>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Horário de Funcionamento</h2>
            <div className={styles.field}>
              <BusinessHoursEditor
                hours={formData?.tenant?.businessHours || {}}
                onChange={(newHours) =>
                  setFormData({
                    ...formData,
                    tenant: {
                      ...formData.tenant,
                      businessHours: newHours,
                    },
                  })
                }
              />
            </div>
          </section>

          {/* Posição histórica do FAQ — mantida para quem TEM agenda. Com o
              Plano Atendimento a seção sobe para o topo do formulário. */}
          {!faqFirst && faqSection}

          {/* ── Templates de Lembrete ──────────────────────────────────── */}
          <section className={styles.section}>
            <button
              type="button"
              className={styles.planToggle}
              aria-expanded={templateExpanded}
              onClick={() => setTemplateExpanded((v) => !v)}
            >
              <span className={styles.label}>Templates de Lembrete</span>
              <span className={styles.toggleIcon}>
                {templateExpanded ? "▾" : "▸"}
              </span>
            </button>

            {templateExpanded && (
              <div style={{ marginTop: "14px" }}>
                <p className={styles.helpText} style={{ marginBottom: "12px" }}>
                  A Meta exige templates pré-aprovados para enviar mensagens
                  proativas (lembretes). Use <strong>{"{{1}}"}</strong> para o
                  nome do cliente, <strong>{"{{2}}"}</strong> para o serviço e{" "}
                  <strong>{"{{3}}"}</strong> para o horário.
                </p>

                {/* Lista de templates */}
                {templates.length > 0 && (
                  <div className={styles.templateList}>
                    {templates.map((tpl) => (
                      <div key={tpl.id} className={styles.templateCard}>
                        <div className={styles.templateCardHeader}>
                          <div>
                            <span className={styles.templateName}>
                              {tpl.displayName}
                            </span>
                            <div className={styles.templateMeta}>
                              Nome Meta: {tpl.name} · {tpl.language}
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: "6px",
                              alignItems: "center",
                            }}
                          >
                            {tpl.isActive && (
                              <span
                                className={`${styles.statusBadge} ${styles.statusActive}`}
                              >
                                Ativo
                              </span>
                            )}
                            <span
                              className={`${styles.statusBadge} ${styles[STATUS_CLASS[tpl.status] || "statusDraft"]}`}
                            >
                              {STATUS_LABEL[tpl.status] || tpl.status}
                            </span>
                          </div>
                        </div>

                        <div className={styles.templateBody}>
                          {tpl.bodyText}
                        </div>

                        {tpl.rejectionReason && (
                          <div className={styles.rejectionReason}>
                            Motivo da rejeição: {tpl.rejectionReason}
                          </div>
                        )}

                        <div className={styles.templateActions}>
                          {tpl.status === "APPROVED" && !tpl.isActive && (
                            <button
                              type="button"
                              className={`${styles.btnAction} ${styles.btnActivate}`}
                              onClick={() => handleActivateTemplate(tpl.id)}
                            >
                              Usar nos lembretes
                            </button>
                          )}
                          {(tpl.status === "DRAFT" ||
                            tpl.status === "REJECTED") && (
                            <button
                              type="button"
                              className={`${styles.btnAction} ${styles.btnSubmit}`}
                              onClick={() => handleSubmitTemplate(tpl.id)}
                            >
                              Enviar para Meta
                            </button>
                          )}
                          {tpl.status === "PENDING" && (
                            <button
                              type="button"
                              className={`${styles.btnAction} ${styles.btnSync}`}
                              onClick={() => handleSyncTemplate(tpl.id)}
                            >
                              Verificar aprovação
                            </button>
                          )}
                          <button
                            type="button"
                            className={`${styles.btnAction} ${styles.btnDelete}`}
                            onClick={() => handleDeleteTemplate(tpl.id)}
                          >
                            Remover
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Formulário de criação — div, não form (evita form aninhado) */}
                {showTemplateForm ? (
                  <div className={styles.templateForm}>
                    <div className={styles.field}>
                      <label className={styles.label}>Nome do Template</label>
                      <input
                        type="text"
                        className={styles.input}
                        placeholder="Ex.: Lembrete de Agendamento"
                        value={templateForm.displayName}
                        onChange={(e) =>
                          setTemplateForm({
                            ...templateForm,
                            displayName: e.target.value,
                          })
                        }
                        onKeyDown={(e) =>
                          e.key === "Enter" && e.preventDefault()
                        }
                      />
                      <p className={styles.helpText}>
                        Nome técnico (gerado automaticamente):{" "}
                        <strong>
                          {templateForm.displayName
                            .toLowerCase()
                            .normalize("NFD")
                            .replace(/[\u0300-\u036f]/g, "")
                            .replace(/[^a-z0-9]+/g, "_")
                            .replace(/^_|_$/g, "") || "—"}
                        </strong>
                      </p>
                    </div>

                    <div className={styles.field}>
                      <label className={styles.label}>Corpo da Mensagem</label>
                      <textarea
                        className={styles.textarea}
                        rows={4}
                        placeholder={
                          "Olá, {{1}}! Lembramos seu agendamento de *{{2}}* amanhã às *{{3}}*. Caso não possa vir, nos avise. Obrigado!"
                        }
                        value={templateForm.bodyText}
                        onChange={(e) =>
                          setTemplateForm({
                            ...templateForm,
                            bodyText: e.target.value,
                          })
                        }
                      />
                      <div className={styles.templateVarHint}>
                        Variáveis disponíveis:
                        <br />
                        <strong>{"{{1}}"}</strong> → Nome do cliente
                        &nbsp;·&nbsp;
                        <strong>{"{{2}}"}</strong> → Serviço &nbsp;·&nbsp;
                        <strong>{"{{3}}"}</strong> → Horário (ex: 14:00)
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: "10px" }}>
                      <button
                        type="button"
                        className={styles.primaryButton}
                        disabled={savingTemplate}
                        style={{ flex: 1 }}
                        onClick={handleCreateTemplate}
                      >
                        {savingTemplate
                          ? "Enviando..."
                          : "Criar e Enviar para Meta"}
                      </button>
                      <button
                        type="button"
                        className={styles.primaryButton}
                        style={{
                          background: "#eee",
                          color: "#333",
                          flex: "0 0 auto",
                        }}
                        onClick={() => setShowTemplateForm(false)}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={styles.primaryButton}
                    style={{ marginTop: "4px" }}
                    onClick={() => setShowTemplateForm(true)}
                  >
                    + Criar Novo Template
                  </button>
                )}
              </div>
            )}
          </section>

          {/* ── Lembrete de Reativação ─────────────────────────────────────── */}
          <section className={styles.section}>
            <button
              type="button"
              className={styles.planToggle}
              aria-expanded={reminderExpanded}
              onClick={() => setReminderExpanded((v) => !v)}
            >
              <span className={styles.label}>
                🔔 Lembrete de Reativação{!schedulingIncluded && " 🔒"}
              </span>
              <span className={styles.toggleIcon}>
                {reminderExpanded ? "▾" : "▸"}
              </span>
            </button>

            {/* O lembrete convida o cliente a AGENDAR e escreve em
                `PATCH /tenants/reminder-settings`, rota gateada por
                `requireScheduling` (ADR-0026 §7). A seção continua visível —
                bloquear, não esconder — mas o miolo vira convite de upgrade. */}
            {reminderExpanded && !schedulingIncluded && (
              <SchedulingUpsellInline
                upgradeUrl={upgradeUrl}
                message="O lembrete de reativação convida seu cliente a marcar um horário — por isso depende da agenda, que não faz parte do Plano Atendimento."
              />
            )}

            {reminderExpanded && schedulingIncluded && (
              <div style={{ marginTop: "14px" }}>
                <p className={styles.helpText} style={{ marginBottom: "16px" }}>
                  Envie uma mensagem automática para clientes que não voltaram há
                  algum tempo, lembrando-os de agendar. O lembrete é enviado
                  automaticamente toda manhã às 10h, incluindo o último serviço
                  agendado e um convite para retornar.
                </p>

                {/* Toggle de ativação */}
                <div className={styles.reportToggleRow}>
                  <label
                    htmlFor="enableReminders"
                    className={styles.reportToggleLabel}
                  >
                    <input
                      id="enableReminders"
                      type="checkbox"
                      role="switch"
                      aria-checked={reminderSettings.enableReminders}
                      className={styles.reportCheckbox}
                      checked={reminderSettings.enableReminders}
                      onChange={(e) =>
                        setReminderSettings((prev) => ({
                          ...prev,
                          enableReminders: e.target.checked,
                        }))
                      }
                    />
                    Ativar lembretes automáticos
                  </label>
                </div>

                {/* Input numérico de dias com stepper −/+ */}
                <div className={styles.field} style={{ marginTop: "12px" }}>
                  <label className={styles.label} htmlFor="reviewReminderDays">
                    Enviar lembrete após (dias sem agendamento)
                  </label>
                  <div className={styles.stepperRow}>
                    <button
                      type="button"
                      className={styles.stepperBtn}
                      aria-label="Diminuir dias"
                      disabled={
                        !reminderSettings.enableReminders ||
                        reminderSettings.reviewReminderDays <= REVIEW_DAYS_MIN
                      }
                      onClick={() =>
                        setReminderSettings((prev) => ({
                          ...prev,
                          reviewReminderDays: clampReviewDays(
                            prev.reviewReminderDays - 1,
                          ),
                        }))
                      }
                    >
                      −
                    </button>
                    <input
                      id="reviewReminderDays"
                      type="number"
                      inputMode="numeric"
                      min={REVIEW_DAYS_MIN}
                      max={REVIEW_DAYS_MAX}
                      className={styles.stepperInput}
                      value={reminderSettings.reviewReminderDays}
                      disabled={!reminderSettings.enableReminders}
                      onChange={(e) =>
                        setReminderSettings((prev) => ({
                          ...prev,
                          reviewReminderDays: e.target.value,
                        }))
                      }
                      onBlur={(e) =>
                        setReminderSettings((prev) => ({
                          ...prev,
                          reviewReminderDays: clampReviewDays(e.target.value),
                        }))
                      }
                      aria-describedby="reviewReminderDays-help"
                    />
                    <button
                      type="button"
                      className={styles.stepperBtn}
                      aria-label="Aumentar dias"
                      disabled={
                        !reminderSettings.enableReminders ||
                        reminderSettings.reviewReminderDays >= REVIEW_DAYS_MAX
                      }
                      onClick={() =>
                        setReminderSettings((prev) => ({
                          ...prev,
                          reviewReminderDays: clampReviewDays(
                            Number(prev.reviewReminderDays) + 1,
                          ),
                        }))
                      }
                    >
                      +
                    </button>
                  </div>
                  <p id="reviewReminderDays-help" className={styles.helpText}>
                    Mínimo: {REVIEW_DAYS_MIN} dias · máximo: {REVIEW_DAYS_MAX} dias.
                  </p>
                </div>

                {/* Nota explicativa: o toggle controla TODOS os lembretes */}
                <p
                  className={styles.helpText}
                  style={{ marginTop: "8px", fontStyle: "italic" }}
                >
                  ℹ️ Ativar ou desativar esta opção afeta todos os lembretes
                  automáticos, incluindo os lembretes de agendamento do dia
                  seguinte.
                </p>

                {/* Botão de salvar */}
                <button
                  type="button"
                  className={styles.primaryButton}
                  style={{ marginTop: "16px" }}
                  disabled={reminderSaving}
                  onClick={handleSaveReminderSettings}
                >
                  {reminderSaving ? "Salvando..." : "Salvar configurações"}
                </button>
              </div>
            )}
          </section>

          {/* ── Pausa do bot após atendimento humano ───────────────────────── */}
          <section className={styles.section}>
            <button
              type="button"
              className={styles.planToggle}
              aria-expanded={humanControlExpanded}
              onClick={() => setHumanControlExpanded((v) => !v)}
            >
              <span className={styles.label}>
                🤝 Pausa do bot após atendimento humano
              </span>
              <span className={styles.toggleIcon}>
                {humanControlExpanded ? "▾" : "▸"}
              </span>
            </button>

            {humanControlExpanded && (
              <div style={{ marginTop: "14px" }}>
                <p className={styles.helpText} style={{ marginBottom: "16px" }}>
                  Depois que alguém da equipe responde manualmente, o bot fica em
                  silêncio nesse contato por este tempo.
                </p>

                {/* Seletor de preset (ou "Personalizado") */}
                <div className={styles.field}>
                  <label
                    className={styles.label}
                    htmlFor="humanControlPreset"
                  >
                    Tempo de pausa
                  </label>
                  <select
                    id="humanControlPreset"
                    className={styles.select}
                    value={humanControlSelectedMs}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "custom") {
                        setHumanControlSelectedMs("custom");
                      } else {
                        const ms = Number(v);
                        setHumanControlSelectedMs(ms);
                        // Mantém o campo custom coerente caso o usuário troque.
                        setHumanControlCustomMinutes(
                          String(Math.round(ms / MS_PER_MINUTE)),
                        );
                      }
                    }}
                    aria-describedby="humanControlPreset-help"
                  >
                    {HUMAN_CONTROL_PRESETS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                    <option value="custom">Personalizado</option>
                  </select>
                  <p id="humanControlPreset-help" className={styles.helpText}>
                    Padrão: 2 h. Escolha um tempo ou defina um valor
                    personalizado.
                  </p>
                </div>

                {/* Campo custom (em minutos) — só quando "Personalizado" */}
                {humanControlSelectedMs === "custom" && (
                  <div className={styles.field} style={{ marginTop: "12px" }}>
                    <label
                      className={styles.label}
                      htmlFor="humanControlCustomMinutes"
                    >
                      Tempo personalizado (em minutos)
                    </label>
                    <input
                      id="humanControlCustomMinutes"
                      type="number"
                      inputMode="numeric"
                      min={HUMAN_CONTROL_MIN_MINUTES}
                      max={HUMAN_CONTROL_MAX_MINUTES}
                      className={styles.input}
                      value={humanControlCustomMinutes}
                      onChange={(e) =>
                        setHumanControlCustomMinutes(e.target.value)
                      }
                      aria-describedby="humanControlCustomMinutes-help"
                    />
                    <p
                      id="humanControlCustomMinutes-help"
                      className={styles.helpText}
                    >
                      Mínimo: {HUMAN_CONTROL_MIN_MINUTES} minutos · máximo:{" "}
                      {HUMAN_CONTROL_MAX_MINUTES} minutos (24 horas).
                    </p>
                  </div>
                )}

                {/* Botão de salvar */}
                <button
                  type="button"
                  className={styles.primaryButton}
                  style={{ marginTop: "16px" }}
                  disabled={humanControlSaving}
                  onClick={handleSaveHumanControlSettings}
                >
                  {humanControlSaving ? "Salvando..." : "Salvar configurações"}
                </button>
              </div>
            )}
          </section>

          {/* ── Relatório Semanal ──────────────────────────────────────────── */}
          <section className={styles.section}>
            <button
              type="button"
              className={styles.planToggle}
              aria-expanded={reportExpanded}
              onClick={() => setReportExpanded((v) => !v)}
            >
              <span className={styles.label}>📊 Relatório Semanal</span>
              <span className={styles.toggleIcon}>
                {reportExpanded ? "▾" : "▸"}
              </span>
            </button>

            {reportExpanded && (
              <div style={{ marginTop: "14px" }}>
                <p className={styles.helpText} style={{ marginBottom: "16px" }}>
                  Receba um resumo semanal de agendamentos e mensagens diretamente
                  no seu WhatsApp toda segunda-feira às 9h.
                </p>

                {/* Toggle de ativação */}
                <div className={styles.reportToggleRow}>
                  <label
                    htmlFor="weeklyReportEnabled"
                    className={styles.reportToggleLabel}
                  >
                    <input
                      id="weeklyReportEnabled"
                      type="checkbox"
                      role="switch"
                      aria-checked={reportSettings.weeklyReportEnabled}
                      className={styles.reportCheckbox}
                      checked={reportSettings.weeklyReportEnabled}
                      onChange={(e) =>
                        setReportSettings((prev) => ({
                          ...prev,
                          weeklyReportEnabled: e.target.checked,
                        }))
                      }
                    />
                    Receber relatório semanal toda segunda-feira às 9h
                  </label>
                </div>

                {/* Aviso quando ativo mas sem número informado */}
                {reportSettings.weeklyReportEnabled &&
                  !reportSettings.reportPhone && (
                    <p
                      className={styles.reportWarning}
                      role="alert"
                      style={{ marginTop: "12px" }}
                    >
                      ⚠️ Informe um número de WhatsApp para receber os relatórios.
                    </p>
                  )}

                {/* Input de telefone (máscara brasileira) */}
                <div
                  className={styles.field}
                  style={{ marginTop: "12px" }}
                >
                  <label
                    className={styles.label}
                    htmlFor="reportPhone"
                  >
                    Enviar para o número
                  </label>
                  <input
                    id="reportPhone"
                    type="tel"
                    inputMode="numeric"
                    className={styles.input}
                    placeholder="(XX) XXXXX-XXXX"
                    value={maskBrPhone(reportSettings.reportPhone)}
                    disabled={!reportSettings.weeklyReportEnabled}
                    maxLength={16}
                    onChange={(e) => {
                      const onlyDigits = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 11);
                      setReportSettings((prev) => ({
                        ...prev,
                        reportPhone: onlyDigits,
                      }));
                    }}
                    aria-describedby="reportPhone-help"
                  />
                  <p id="reportPhone-help" className={styles.helpText}>
                    Deixe em branco para usar o número conectado ao WhatsApp.
                  </p>
                </div>

                {/* Botão de salvar */}
                <button
                  type="button"
                  className={styles.primaryButton}
                  style={{ marginTop: "16px" }}
                  disabled={reportSaving}
                  onClick={handleSaveReportSettings}
                >
                  {reportSaving ? "Salvando..." : "Salvar configurações"}
                </button>
              </div>
            )}
          </section>

          {/* ── Desconectar número do Nexus (STANDALONE) ──────────────────────
              Independente do modal de cancelamento de assinatura. Só aparece
              quando há número Meta vinculado (senão não há o que desconectar).
              Coexistência → instruções no app; Tradicional → deregister. */}
          {hasMetaNumber && (
            <section className={styles.section}>
              <button
                type="button"
                className={styles.planToggle}
                aria-expanded={disconnectExpanded}
                onClick={() => setDisconnectExpanded((v) => !v)}
              >
                <span className={styles.label}>🔌 Desconectar número</span>
                <span className={styles.toggleIcon}>
                  {disconnectExpanded ? "▾" : "▸"}
                </span>
              </button>

              {disconnectExpanded && (
                <div style={{ marginTop: "14px" }}>
                  <h3 className={styles.disconnectTitle}>
                    Desconectar este número do Nexus
                  </h3>

                  {isCoexistence ? (
                    <>
                      {/* CASO A — Coexistência: não chamamos deregister. */}
                      <p
                        className={styles.helpText}
                        style={{ marginBottom: "12px" }}
                      >
                        Como seu número usa o modo Coexistência (funciona no app
                        e na nossa plataforma ao mesmo tempo), a desconexão é
                        feita por você, no app WhatsApp Business:
                      </p>
                      <ol className={styles.disconnectSteps}>
                        <li>
                          Abra o WhatsApp Business no celular onde o número está
                          ativo.
                        </li>
                        <li>
                          Vá em Configurações &gt; Conta &gt; Plataforma de
                          Negócios.
                        </li>
                        <li>Toque em &apos;Desconectar conta&apos; e confirme.</li>
                      </ol>
                      <p
                        className={styles.helpText}
                        style={{ marginTop: "12px", marginBottom: "16px" }}
                      >
                        Seu número continua funcionando normalmente no app — só a
                        integração com o Nexus é encerrada. Não desinstale o app
                        (isso desconecta tudo). Assim que você desconectar,
                        detectamos automaticamente e atualizamos o status aqui.
                      </p>
                      <button
                        type="button"
                        className={styles.primaryButton}
                        disabled={checkingStatus}
                        onClick={handleRefreshWhatsappStatus}
                      >
                        {checkingStatus
                          ? "Verificando..."
                          : "Já desconectei — verificar status"}
                      </button>
                    </>
                  ) : (
                    <>
                      {/* CASO B — Tradicional: botão real + ConfirmDialog. */}
                      <p
                        className={styles.helpText}
                        style={{ marginBottom: "16px" }}
                      >
                        Ao desconectar, o bot para de atender por este número e a
                        integração com a Meta é encerrada. Você poderá reconectar
                        depois pelo onboarding.
                      </p>
                      <button
                        type="button"
                        className={styles.disconnectButton}
                        disabled={disconnecting}
                        onClick={() => setShowDisconnectConfirm(true)}
                      >
                        {disconnecting
                          ? "Desconectando..."
                          : "Desconectar número"}
                      </button>
                    </>
                  )}
                </div>
              )}
            </section>
          )}

          {/* ── Integração NextFit (somente segmento "academias") ─────────── */}
          {isAcademia && (
            <section className={styles.section}>
              <button
                type="button"
                className={styles.planToggle}
                aria-expanded={nextfitExpanded}
                onClick={() => setNextfitExpanded((v) => !v)}
              >
                <span className={styles.label}>🔗 Integração NextFit</span>
                <span
                  className={`${styles.statusBadge} ${
                    hasNextfitKey
                      ? styles.nextfitConnected
                      : styles.nextfitNotConfigured
                  }`}
                  role="status"
                  aria-live="polite"
                  style={{ marginLeft: "auto", marginRight: "10px" }}
                >
                  {hasNextfitKey ? "✅ Conectado" : "⚠️ Não configurado"}
                </span>
                <span className={styles.toggleIcon}>
                  {nextfitExpanded ? "▾" : "▸"}
                </span>
              </button>

              {nextfitExpanded && (
                <div style={{ marginTop: "14px" }}>
                  <p
                    className={styles.helpText}
                    style={{ marginBottom: "16px" }}
                  >
                    Conecte seu sistema NextFit para o assistente consultar
                    aulas, turmas e contratos em tempo real.
                  </p>

                  <div className={styles.field}>
                    <label className={styles.label} htmlFor="nextfitApiKey">
                      API Key da NextFit
                    </label>
                    <input
                      id="nextfitApiKey"
                      type="password"
                      autoComplete="off"
                      className={styles.input}
                      placeholder={
                        hasNextfitKey
                          ? "••••••••••••"
                          : "Cole aqui sua API Key da NextFit"
                      }
                      value={nextfitApiKey}
                      onChange={(e) => setNextfitApiKey(e.target.value)}
                      aria-describedby="nextfitApiKey-help"
                    />
                    <p id="nextfitApiKey-help" className={styles.helpText}>
                      Como obter sua API Key: acesse NextFit → Loja → API Next
                      Fit → Ativar.
                      {hasNextfitKey &&
                        " Sua chave já está configurada; preencha apenas para substituí-la."}
                    </p>
                  </div>

                  <div className={styles.field}>
                    <label
                      className={styles.label}
                      htmlFor="nextfitSchedulingUrl"
                    >
                      Link de agendamento
                    </label>
                    <input
                      id="nextfitSchedulingUrl"
                      type="url"
                      className={styles.input}
                      placeholder="https://..."
                      value={nextfitSchedulingUrl}
                      onChange={(e) =>
                        setNextfitSchedulingUrl(e.target.value)
                      }
                      aria-describedby="nextfitSchedulingUrl-help"
                    />
                    <p
                      id="nextfitSchedulingUrl-help"
                      className={styles.helpText}
                    >
                      Link que o assistente envia ao cliente quando ele pede
                      para agendar (ex.: página de agendamento da sua academia
                      ou do NextFit).
                    </p>
                  </div>

                  <button
                    type="button"
                    className={styles.primaryButton}
                    style={{ marginTop: "16px" }}
                    disabled={nextfitSaving}
                    onClick={handleSaveNextfitSettings}
                  >
                    {nextfitSaving ? "Salvando..." : "Salvar"}
                  </button>
                </div>
              )}
            </section>
          )}

          <ButtonSave type="submit" isLoading={saving} disabled={!canSave}>
            Salvar Alterações
          </ButtonSave>

          {!isDirty && (
            <p className={styles.subtleNote} role="status" aria-live="polite">
              Faça uma alteração para habilitar o botão de salvar.
            </p>
          )}
        </form>
      </div>

      {/* Confirmação da desconexão (caso tradicional). Reusa o ConfirmDialog
          acessível do design system — não acoplado ao modal de cancelamento. */}
      <ConfirmDialog
        isOpen={showDisconnectConfirm}
        title="Desconectar número?"
        message="O bot vai parar de atender por este número imediatamente. Tem certeza?"
        confirmLabel="Sim, desconectar"
        cancelLabel="Cancelar"
        confirmVariant="danger"
        confirmDisabled={disconnecting}
        onConfirm={handleDisconnectNumber}
        onCancel={() => {
          if (!disconnecting) setShowDisconnectConfirm(false);
        }}
      />
    </div>
  );
}
